import { html, TemplateResult } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { RmTopIdentityRisksBySeverityPayload } from '@nirvana/shared/dtos/types/widgets/common';
import { getRoutinesTranslator } from '../../../common/translations';
import { DEFAULT_RM_WIDGET_TOP_ROWS } from '@nirvana/shared/constants';
import { WEB_COMPONENT_GM_RM_TOP_IDENTITY_RISKS_SEVERITY_WIDGET } from '../../../../webcomponents.constants';
import { Row, RowConfig } from '../../../../ui/gzBasicTable/types/types';
import { renderText } from '../../text';
import { fillArray } from '@nirvana/shared/basic-types/array';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { ColumnKeys as UserBehaviourColumnRisksKeys } from '../../../userBehaviorRisksApp/types';
import { translateFn } from '../../../../utils/translator.helper';
import { FontSizes } from '../../../../types/common/types';
import { LinkType } from '../../../../ui/gzLinkComponent/types';
import { State } from '../../../common/constants';
import { computeMaxRowsFit, fitItemsToWidget } from '../../resize';
import { renderIdentityMonitoringDisabled } from '../../identity.monitoring';

/**
 * @summary Type here the description of this component
 * @tag gz-rm-top-identity-risks-severity-widget
 */
@customElement(WEB_COMPONENT_GM_RM_TOP_IDENTITY_RISKS_SEVERITY_WIDGET)
export class GzRmTopIdentityRisksSeverityWidgetWebComponent extends GzGenericWidgetContent {
  @property({ type: WidgetStateFrontEnd, attribute: true, reflect: true })
  state: WidgetStateFrontEnd;

  @property({ type: Object, attribute: true })
  payload: RmTopIdentityRisksBySeverityPayload = { topIdentityRisksBySeverity: { companyId: '', total: 0, items: [], identityMonitoringDisabled: false } };

  routinesTranslator = getRoutinesTranslator();

  static get styles() {
    return styles;
  }

  protected viewAll(): void {
    NavigationHelper.navigateToRiskManagement({
      company: { id: this.payload.topIdentityRisksBySeverity?.companyId ?? '' },
      filters: [
        {
          [UserBehaviourColumnRisksKeys.STATE]: { in: [State.ACTIVE] },
        },
        {
          [UserBehaviourColumnRisksKeys.RISK_SCORE]: { range: { min: 1, max: 100 } },
        },
      ],
      section: 'identity-risks',
      newTab: true,
    });
  }

  protected renderLoader(): TemplateResult {
    return html` <gz-loader class="pt-1" .rows="${computeMaxRowsFit(this.contentHeight)}" columns="1"></gz-loader>`;
  }

  protected renderContent(): TemplateResult {
    if (this.payload.topIdentityRisksBySeverity?.identityMonitoringDisabled) {
      return renderIdentityMonitoringDisabled();
    }

    const items: Row[] =
      this.payload.topIdentityRisksBySeverity?.items?.map(
        ({ id, score }): RowConfig => ({
          data: { indicatorId: id },
          cells: [
            renderText(this.routinesTranslator.translateRoutineName(id, true)),
            html` <gz-rm-severity .score="${score}" reverse></gz-rm-severity>`,
          ],
        })
      ) ?? [];

    const emptyItem = [renderText('...', true), html` <gz-rm-severity noHighlight reverse></gz-rm-severity>`];
    const fullItems = fitItemsToWidget(fillArray(items, DEFAULT_RM_WIDGET_TOP_ROWS, emptyItem), this.contentHeight);

    return html`
      <gz-basic-table
        style="margin-top: calc(var(--spacer)*0.3)"
        rowsClickable
        alignLastColumnRight
        .onClick="${(rowData: { indicatorId: number }) => {
          if (!rowData) {
            return;
          }

          const { indicatorId } = rowData;

          NavigationHelper.navigateToRiskManagement({
            company: { id: this.payload.topIdentityRisksBySeverity?.companyId ?? '' },
            section: 'identity-risks',
            filters: [
              {
                [UserBehaviourColumnRisksKeys.USER_BEHAVIOR_RISK]: {
                  complexEnumFilter: [{ value: indicatorId }],
                },
              },
            ],
            newTab: true,
          });
        }}"
        .data="${fullItems}"
      ></gz-basic-table>

      <gz-link type="${LinkType.QUIET}" fontSize="${FontSizes.PARAGRAPH_11}" .callback=${() => this.viewAll()}>
        ${translateFn('View all')} (${this.payload.topIdentityRisksBySeverity?.total ?? 0})
      </gz-link>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-rm-top-identity-risks-severity-widget': GzRmTopIdentityRisksSeverityWidgetWebComponent;
  }
}
